import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import Editor from '@monaco-editor/react';
import { useMemo, useState } from 'react';
import jsonata from 'jsonata';
import { providerApi, type ProviderUpdateRequestDto } from '../api/admin';
import { messageFromOrchestratorAxiosError } from '../lib/axiosErrorMessage';

/**
 * ProviderDetailPage
 * ------------------
 * Edit pane on the left, JSONata playground on the right. The
 * playground evaluates the candidate mapping in two places:
 *
 *   1. Locally, in the browser, on every keystroke — sub-50ms
 *      feedback as the operator types.
 *
 *   2. Server-side, via `POST /providers/:id/preview`, when the
 *      operator clicks "Validate". The server pass is the
 *      authoritative check because it runs the same Zod schemas
 *      the doctrine engine uses.
 *
 * "Save" persists the mapping via `PUT /providers/:id`. The
 * orchestrator's mapper compile cache invalidates automatically on
 * `updated_at`, so the next engine call after save picks up the new
 * shape without a separate flush.
 */
export function ProviderDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const provider = useQuery({
    queryKey: ['provider', id],
    queryFn: () => providerApi.get(id),
  });

  const [tab, setTab] = useState(0);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [baseUrl, setBaseUrl] = useState<string | null>(null);
  const [isActive, setIsActive] = useState<boolean | null>(null);
  const [mappingJson, setMappingJson] = useState<string | null>(null);
  const [sampleJson, setSampleJson] = useState<string>('{\n  \n}');
  const [serverResult, setServerResult] = useState<{
    output: unknown;
    valid: boolean;
    errors?: string[];
    schema?: string | null;
  } | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const liveDisplayName = displayName ?? provider.data?.displayName ?? '';
  const liveBaseUrl = baseUrl ?? provider.data?.baseUrl ?? '';
  const liveIsActive = isActive ?? provider.data?.isActive ?? false;
  const liveMappingJson = useMemo(() => {
    if (mappingJson !== null) return mappingJson;
    return JSON.stringify(provider.data?.responseMapping ?? {}, null, 2);
  }, [mappingJson, provider.data?.responseMapping]);

  const localPreview = useLocalPreview(liveMappingJson, sampleJson);

  const update = useMutation({
    mutationFn: async () => {
      const patch: Record<string, unknown> = {};
      if (displayName !== null) patch.displayName = displayName;
      if (baseUrl !== null) patch.baseUrl = baseUrl;
      if (isActive !== null) patch.isActive = isActive;
      if (mappingJson !== null) {
        patch.responseMapping = JSON.parse(mappingJson || '{}');
      }
      return providerApi.update(id, patch as ProviderUpdateRequestDto);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['provider', id] });
      qc.invalidateQueries({ queryKey: ['providers'] });
    },
  });

  const serverValidate = useMutation({
    mutationFn: async () => {
      setServerError(null);
      let mapping: Record<string, string> | null = null;
      try {
        mapping = JSON.parse(liveMappingJson || '{}');
      } catch (e) {
        setServerError(`Mapping JSON invalid: ${e instanceof Error ? e.message : String(e)}`);
        throw e;
      }
      let sample: unknown;
      try {
        sample = JSON.parse(sampleJson || 'null');
      } catch (e) {
        setServerError(`Sample JSON invalid: ${e instanceof Error ? e.message : String(e)}`);
        throw e;
      }
      return providerApi.preview(id, {
        sampleResponse: sample,
        responseMapping: mapping,
      });
    },
    onSuccess: (data) => setServerResult(data),
    onError: (err) => {
      setServerError((prev) => prev || messageFromOrchestratorAxiosError(err) || 'Preview failed.');
    },
  });

  if (provider.isLoading) return <Typography>Loading…</Typography>;
  if (!provider.data) return <Typography>Not found.</Typography>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {provider.data.displayName}
        </Typography>
        <Chip size="small" label={provider.data.id} />
        <Chip size="small" label={provider.data.adapterKind} />
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Settings" />
        <Tab label="Response Mapping (JSONata)" />
      </Tabs>

      {tab === 0 && (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Display name"
                value={liveDisplayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <TextField
                size="small"
                label="Base URL"
                value={liveBaseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                helperText="Honoured by the doctrine engine when calling this provider; falls back to SERVICE_BASE_URLS only when blank."
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={liveIsActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label="Active"
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={() => update.mutate()}
                  disabled={update.isPending}
                >
                  Save
                </Button>
                {update.isSuccess && (
                  <Typography variant="caption" color="success.main" sx={{ alignSelf: 'center' }}>
                    Saved.
                  </Typography>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {tab === 1 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Mapping document
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Each value is a JSONata expression evaluated against the vendor response.
              </Typography>
              <Box sx={{ border: 1, borderColor: 'divider', mt: 1 }}>
                <Editor
                  height="240px"
                  language="json"
                  value={liveMappingJson}
                  onChange={(v) => setMappingJson(v ?? '')}
                  options={{ minimap: { enabled: false }, fontSize: 12 }}
                />
              </Box>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Sample vendor response
              </Typography>
              <Box sx={{ border: 1, borderColor: 'divider', mt: 1 }}>
                <Editor
                  height="240px"
                  language="json"
                  value={sampleJson}
                  onChange={(v) => setSampleJson(v ?? '')}
                  options={{ minimap: { enabled: false }, fontSize: 12 }}
                />
              </Box>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => serverValidate.mutate()}
                  disabled={serverValidate.isPending}
                >
                  Validate via server
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => update.mutate()}
                  disabled={update.isPending}
                >
                  Save mapping
                </Button>
              </Stack>
              {serverError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {serverError}
                </Alert>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="subtitle2">Local preview</Typography>
              <Typography variant="caption" color="text.secondary">
                Live as you type. Schema validation happens on the server.
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  border: 1,
                  borderColor: 'divider',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  maxHeight: 220,
                  overflow: 'auto',
                  backgroundColor: 'grey.50',
                }}
              >
                {localPreview.error ? (
                  <span style={{ color: '#b00020' }}>{localPreview.error}</span>
                ) : (
                  <pre style={{ margin: 0 }}>
                    {JSON.stringify(localPreview.output, null, 2)}
                  </pre>
                )}
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2">Server preview</Typography>
              {serverResult ? (
                <>
                  <Typography variant="caption" color="text.secondary">
                    Schema: {serverResult.schema ?? 'unknown'} ·{' '}
                    {serverResult.valid ? (
                      <span style={{ color: '#2e7d32' }}>VALID</span>
                    ) : (
                      <span style={{ color: '#b00020' }}>INVALID</span>
                    )}
                  </Typography>
                  {serverResult.errors && serverResult.errors.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {serverResult.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                  <Box
                    sx={{
                      mt: 1,
                      p: 1,
                      border: 1,
                      borderColor: 'divider',
                      fontFamily: 'monospace',
                      fontSize: 12,
                      maxHeight: 200,
                      overflow: 'auto',
                      backgroundColor: 'grey.50',
                    }}
                  >
                    <pre style={{ margin: 0 }}>
                      {JSON.stringify(serverResult.output, null, 2)}
                    </pre>
                  </Box>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Click "Validate via server" to run the canonical check.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
}

/**
 * Local-only JSONata evaluator. Returns either the canonical
 * output (each mapping field evaluated against the sample) or a
 * single error message describing the first failing expression.
 */
function useLocalPreview(
  mappingJson: string,
  sampleJson: string,
): { output: Record<string, unknown> | null; error: string | null } {
  return useMemo(() => {
    let mapping: Record<string, string>;
    try {
      mapping = JSON.parse(mappingJson || '{}');
    } catch (e) {
      return { output: null, error: `Mapping JSON invalid: ${e instanceof Error ? e.message : String(e)}` };
    }
    let sample: unknown;
    try {
      sample = JSON.parse(sampleJson || 'null');
    } catch (e) {
      return { output: null, error: `Sample JSON invalid: ${e instanceof Error ? e.message : String(e)}` };
    }
    const out: Record<string, unknown> = {};
    try {
      for (const [k, expr] of Object.entries(mapping)) {
        const evaluator = jsonata(expr);
        // jsonata 2.x returns a thenable; resolve synchronously when possible.
        // We `await` is not allowed in useMemo, so we tap into the sync
        // path that the library uses for simple expressions. A failing
        // expression throws synchronously.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ev = evaluator.evaluate(sample) as unknown as Promise<unknown> | unknown;
        if (ev && typeof (ev as Promise<unknown>).then === 'function') {
          // Fall back to undefined for async expressions; the server
          // side preview is authoritative for those.
          out[k] = undefined;
        } else {
          out[k] = ev;
        }
      }
    } catch (e) {
      return { output: null, error: e instanceof Error ? e.message : String(e) };
    }
    return { output: out, error: null };
  }, [mappingJson, sampleJson]);
}
